import { Injectable, inject } from '@angular/core';
import {
    BehaviorSubject,
    Subscription,
    filter,
    lastValueFrom,
    map,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { CruiseEvents, Docks, DTDdockDetails, Port } from '../models';
import { CruiseCalendarApiService } from '../services';

@Injectable({
    providedIn: 'root',
})
export class CalendarScheduleState {
    private calendarSubscription: Subscription | undefined;
    private dockSubscription: Subscription | undefined;
    private portSubscription: Subscription | undefined;
    private today = new Date();
    cruiseCalendarApiService = inject(CruiseCalendarApiService);
    initialPort: number | undefined;

    calendarConfig$ = new BehaviorSubject<{
        startDate: Date;
        endDate: Date;
        portId: number;
        excludeCanceled: boolean;
    }>({
        startDate: new Date(this.today.getFullYear(), this.today.getMonth(), 1),
        endDate: new Date(
            this.today.getFullYear() + 1,
            this.today.getMonth(),
            this.today.getDate()
        ),
        portId: 0, // TODO: we'll need to set this up to use the correct port
        excludeCanceled: true,
    });

    calendarEvents$ = new BehaviorSubject<CruiseEvents[]>([]);
    docks$ = new BehaviorSubject<Docks[]>([]);
    ports$ = new BehaviorSubject<Port[]>([]);
    isLoading$ = new BehaviorSubject<boolean>(false);
    refreshRequestedTimestamp$ = new BehaviorSubject<number>(
        new Date().getTime()
    );

    initCalendarEvents(): void {
        if (this.calendarSubscription) {
            return;
        }

        this.calendarSubscription = this.cruiseCalendarApiService
            .loadCruiseScheduleCalendar(
                '72aca93d-62a8-48e8-abf5-9814dc7604ae',
                '2023-10-01',
                '2024-10-01',
                1,
                true
            )
            .subscribe({
                next: (result) => {
                    this.calendarEvents$.next(result.data);
                    return result.data;
                },
                error: (error) => {
                    console.error('Error:', error);
                },
            });
    }

    initAgentDocks(): void {
        if (this.dockSubscription) {
            return;
        }

        this.calendarSubscription = this.cruiseCalendarApiService
            .getAgentDocks(56)
            .subscribe({
                next: (result) => {
                    this.docks$.next(result.data);
                    return result.data;
                },
                error: (error) => {
                    console.error('Error:', error);
                },
            });
    }

    // initAgentDocks(): void {
    //     if (this.dockSubscription) {
    //         return;
    //     }

    //     this.dockSubscription =

    // this.profileState.agentProfile$
    //     .pipe(
    //         map((res) => {
    //             return res?.partnerId;
    //         }),
    //         filter((res) => !!res && typeof res === 'number'),
    //         take(1),
    //         switchMap((partnerId) => {
    //             this.docks$.next([]);
    //             return this.apiAgentService
    //                 .getAgentDocks(Number(partnerId))
    //                 .pipe(
    //                     map((res) => {
    //                         return res.data.map((dock) => {
    //                             return {
    //                                 ...dock,
    //                                 label: `${dock.dockName} - ${dock.portName}`,
    //                             };
    //                         });
    //                     })
    //                 );
    //         })
    //     )
    //     .subscribe((docks) => {
    //         this.docks$.next(docks);
    //     });
    // }

    // initAgentPorts(): void {
    //     if (this.portSubscription) {
    //         return;
    //     }

    //     this.portSubscription =
    // this.profileState.agentProfile$
    //     .pipe(
    //         map((res) => {
    //             return res?.partnerId;
    //         }),
    //         filter((res) => !!res && typeof res === 'number'),
    //         take(1),
    //         switchMap((partnerId) => {
    //             this.ports$.next([]);
    //             return this.apiAgentService
    //                 .getAgentPorts(Number(partnerId))
    //                 .pipe(
    //                     map((res) => {
    //                         return res.data;
    //                     })
    //                 );
    //         })
    //     )
    //     .subscribe((ports) => {
    //         this.calendarConfig$.next({
    //             ...this.calendarConfig$.value,
    //             portId: ports[ports.length - 1].value,
    //         });
    //         this.ports$.next(ports);
    //     });
    // }

    refreshCalendarData(): void {
        this.refreshRequestedTimestamp$.next(new Date().getTime());
    }

    // updateDtdAssignment(dockAssignmemtDetails: DTDdockDetails): Promise<void> {
    //     return lastValueFrom(
    //         this.apiDtdAssignmentService
    //             .saveDockAssignmentDetail(
    //                 dockAssignmemtDetails.companyUniqueID,
    //                 dockAssignmemtDetails.portId,
    //                 dockAssignmemtDetails.assignmentDate,
    //                 dockAssignmemtDetails.shipId,
    //                 dockAssignmemtDetails.dockId
    //             )
    //             .pipe(
    //                 map((res) => {
    //                     if (!res.success) {
    //                         throw res.error;
    //                     }
    //                     return res.data;
    //                 })
    //             )
    //     ).then(() => {
    //         this.refreshCalendarData();
    //         return Promise.resolve();
    //     });
    // }

    updatePortId(portId: number) {
        const calendarConfig = this.calendarConfig$.getValue();
        this.calendarConfig$.next({
            ...calendarConfig,
            portId: portId,
        });
    }

    updateExcludeCanceled(value: boolean) {
        const calendarConfig = this.calendarConfig$.getValue();
        this.calendarConfig$.next({
            ...calendarConfig,
            excludeCanceled: value,
        });
    }

    updateStartDate(startDate: Date) {
        const calendarConfig = this.calendarConfig$.getValue();
        this.calendarConfig$.next({
            ...calendarConfig,
            startDate: startDate,
        });
    }

    updateEndDate(endDate: Date) {
        const calendarConfig = this.calendarConfig$.getValue();
        this.calendarConfig$.next({
            ...calendarConfig,
            endDate: endDate,
        });
    }

    formatDateToYYYYMMDD(inputDate: string) {
        // Split the input date into month, day, and year components
        const [month, day, year] = inputDate.split('/');

        // Create a new date string in "YYYY-MM-DD" format
        const formattedDate = `${year}-${month}-${day}`;

        return formattedDate;
    }
}
